import { FileSchema } from '@ametista-ai/api-schema';
import { FileUploadProps, UploadUrlParams } from '@ametista-ai/api-schema/http/types';
import { heicTo, isHeic } from 'heic-to';
import { decode, decodeImage, toRGBA8 } from 'utif2';
import { apiClient } from '@/stores/api/apiClient';
import { fileUploadQueue } from '@/stores/fileUploadQueue';
import { IconName } from '@/ui/Icon';

export function getFileUrl(file: FileSchema) {
  if (file.publicUrl) {
    return file.publicUrl;
  }

  if (file.url?.startsWith('http')) {
    return file.url;
  }

  return getFileUrlFromId(file.id);
}

export function getFileUrlFromId(fileId: string) {
  return `/api/files/${fileId}`;
}

export function getUserImageUrl(userId: string, suffix = '') {
  return `/api/v1/users/${userId}/picture${suffix}`;
}

export function getAgencyImageUrl(agencyId: string, suffix = '') {
  return `/api/v1/agencies/${agencyId}/image${suffix}`;
}

export function getClientImageUrl(clientId: string, suffix = '') {
  return `/api/v1/clients/${clientId}/image${suffix}`;
}

export function getFileRedirectUrl(path: string) {
  return `/api/files/redirect?path=${path}`;
}

export function triggerFileDownload(url: string, fileName?: string) {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  if (fileName) {
    link.download = fileName;
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function isIosDevice() {
  if (typeof navigator === 'undefined') return false;
  // iPadOS 13+ reports itself as a Mac, so also check for touch support.
  return (
    /iPhone|iPod|iPad/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * iOS Safari ignores the `download` attribute on cross-origin links and just opens the file in a
 * tab. On iOS we instead fetch the file and hand it to the native share sheet, which offers
 * "Save Video" / "Save Image" (straight to Photos) and "Save to Files". Falls back to a regular
 * download if sharing isn't possible (unsupported, bucket CORS not configured, etc).
 */
export async function downloadFile(url: string, fileName?: string, mimeType?: string) {
  if (isIosDevice() && typeof navigator.canShare === 'function') {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const file = new File([blob], fileName || 'download', {
        type: mimeType || blob.type || 'application/octet-stream',
      });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
        return;
      }
    } catch (error) {
      // The user dismissing the share sheet is not a failure.
      if (error instanceof DOMException && error.name === 'AbortError') return;
    }
  }
  triggerFileDownload(url, fileName);
}

const FILE_TYPE_ICON_BY_EXACT_MIME: Record<string, IconName> = {
  'application/pdf': 'fileTypePdf',
  'application/msword': 'fileTypeDoc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'fileTypeDoc',
  'application/vnd.ms-excel': 'fileTypeXls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'fileTypeXls',
  'text/csv': 'fileTypeCsv',
  'application/vnd.ms-powerpoint': 'fileTypePpt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'fileTypePpt',
  'text/plain': 'fileTypeTxt',
  'application/zip': 'fileTypeZip',
  'application/x-zip-compressed': 'fileTypeZip',
};

export function getFileTypeIcon(type: string | undefined): IconName {
  if (!type) {
    return 'file';
  }
  if (FILE_TYPE_ICON_BY_EXACT_MIME[type]) {
    return FILE_TYPE_ICON_BY_EXACT_MIME[type];
  }
  if (type.startsWith('image/')) {
    return 'photo';
  }
  if (type.startsWith('video/')) {
    return 'movie';
  }
  if (type.startsWith('audio/')) {
    return 'fileMusic';
  }
  return 'file';
}

function replaceFileExtension(fileName: string, newExtension: string) {
  const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
  const sanitizedName = nameWithoutExtension || 'image';
  return `${sanitizedName}${newExtension}`;
}

async function convertHeicFileToJpeg(file: File) {
  const jpegBlob = await heicTo({
    blob: file,
    type: 'image/jpeg',
    quality: 1,
  });

  return new File([jpegBlob], replaceFileExtension(file.name, '.jpg'), {
    type: 'image/jpeg',
    lastModified: file.lastModified,
  });
}

const TIFF_MIME_TYPES = new Set(['image/tiff', 'image/tif', 'image/x-tiff', 'image/x-tif']);

function isTiff(file: File) {
  return TIFF_MIME_TYPES.has(file.type) || /\.tiff?$/i.test(file.name);
}

async function convertTiffFileToPng(file: File) {
  const buffer = await file.arrayBuffer();
  const [ifd] = decode(buffer);
  decodeImage(buffer, ifd);
  const rgba = toRGBA8(ifd);

  const canvas = document.createElement('canvas');
  canvas.width = ifd.width;
  canvas.height = ifd.height;
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D context is not available');
  }
  context.putImageData(new ImageData(new Uint8ClampedArray(rgba), ifd.width, ifd.height), 0, 0);

  const pngBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to convert TIFF file to PNG'));
      }
    }, 'image/png');
  });

  return new File([pngBlob], replaceFileExtension(file.name, '.png'), {
    type: 'image/png',
    lastModified: file.lastModified,
  });
}

export async function ensureUploadReadyFile(file: File) {
  if (await isHeic(file)) {
    return convertHeicFileToJpeg(file);
  }

  if (isTiff(file)) {
    return convertTiffFileToPng(file);
  }

  return file;
}

export async function ensureUploadReadyFiles(files: File[]) {
  return Promise.all(files.map((file) => ensureUploadReadyFile(file)));
}

async function uploadAgencyFile(file: File, folderId?: string | null) {
  const fileProps: FileUploadProps = {
    fileName: file.name,
    contentType: file.type,
    fileSize: file.size,
  };

  const uploadResponse = await apiClient.post<{
    data: { file: FileSchema; uploadData: UploadUrlParams };
  }>('/files', { file: fileProps, ...(folderId ? { folderId } : {}) });

  fileUploadQueue.add(file, uploadResponse.data.uploadData, {
    fileObj: uploadResponse.data.file,
    refId: uploadResponse.data.file.id,
  });

  return uploadResponse.data.file;
}

export async function uploadAgencyFiles(files: File[], folderId?: string | null) {
  const normalizedFiles = await ensureUploadReadyFiles(files);
  const uploaded: FileSchema[] = [];
  for (const file of normalizedFiles) {
    uploaded.push(await uploadAgencyFile(file, folderId));
  }
  return uploaded;
}
